// apps/web/src/admin/CallLogTable.tsx
// Presentational only: the /admin Calls tab feeds it, and the PDF export reuses
// callDuration() so both render a call's length identically.

/** Mirrors CallLogRow in apps/backend/src/lib/callLog.ts. The web app has no
 *  import path into the backend workspace, so the shape is restated here; the
 *  field names must stay in step with that file. */
export type CallLogRow = {
  id: number;
  room: string;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  learnerName: string | null;
  guideName: string | null;
  billedMinutes: number;
  guideEarnedDollars: number;
  guideStars: number | null;
  learnerStars: number | null;
};

function mmss(sec: number): string {
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

/** Prefers the duration the LiveKit webhook stored; falls back to the
 *  timestamps when a call ended without one. A live call has no length yet. */
export function callDuration(c: CallLogRow): string {
  if (!c.endedAt) return 'in progress';
  if (c.durationSec != null) return mmss(c.durationSec);
  const ms = new Date(c.endedAt).getTime() - new Date(c.startedAt).getTime();
  return mmss(Math.max(0, Math.round(ms / 1000)));
}

const stars = (n: number | null) => (n != null ? `${n}★` : '—');

export function CallLogTable({ calls, totalInWindow }: {
  calls: CallLogRow[];
  totalInWindow: number;
}) {
  if (calls.length === 0) {
    return <p className="text-xs text-ink-500">No calls in the last 30 days.</p>;
  }
  return (
    <>
      <table className="w-full text-xs">
        <thead className="text-left text-ink-500">
          <tr>
            <th>When</th><th>Learner</th><th>Guide</th><th>Duration</th>
            <th>Billed</th><th>Guide earned</th><th>Guide ★</th><th>Learner ★</th>
          </tr>
        </thead>
        <tbody>
          {calls.map(c => (
            <tr key={c.id}>
              <td>{new Date(c.startedAt).toLocaleString()}</td>
              <td>{c.learnerName ?? '—'}</td>
              <td>{c.guideName ?? '—'}</td>
              <td>{callDuration(c)}</td>
              <td>{c.billedMinutes} min</td>
              <td>${c.guideEarnedDollars.toFixed(2)}</td>
              <td>{stars(c.guideStars)}</td>
              <td>{stars(c.learnerStars)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalInWindow > calls.length && (
        <p className="mt-2 text-xs text-ink-500">
          Showing the {calls.length} most recent of {totalInWindow}.
        </p>
      )}
    </>
  );
}
